import { Pipe, type PipeTransform } from '@angular/core';
import {
    BuildingSubPremiseType,
    type BuildingPrimaryPropertyType,
    type BuildingSubPremiseType as BuildingSubPremiseTypeValue,
} from '@quipex/shared/contracts';
import type { QPX_LUCIDE_ICONS } from '../directives/icon/lucide-icons';
import { getBuildingPropertyTypeIcon } from './building-property-type-icon.pipe';

type IconName = keyof typeof QPX_LUCIDE_ICONS;

const icons = {
    [BuildingSubPremiseType.Unknown]: 'lucideCircleQuestionMark',
    [BuildingSubPremiseType.Antenna]: 'lucideAntenna',
    [BuildingSubPremiseType.Apartment]: 'qpxSubPremiseApartment',
    [BuildingSubPremiseType.AutomatedTellerMachine]:
        'qpxSubPremiseAutomatedTellerMachine',
    [BuildingSubPremiseType.Barbecue]: 'qpxSubPremiseBarbecue',
    [BuildingSubPremiseType.Block]: 'lucideLandPlot',
    [BuildingSubPremiseType.Boatshed]: 'qpxSubPremiseBoatshed',
    [BuildingSubPremiseType.Building]: 'lucideBuilding2',
    [BuildingSubPremiseType.Bungalow]: 'qpxSubPremiseBungalow',
    [BuildingSubPremiseType.Cage]: 'qpxSubPremiseCage',
    [BuildingSubPremiseType.Carpark]: 'lucideSquareParking',
    [BuildingSubPremiseType.Carspace]: 'qpxSubPremiseCarspace',
    [BuildingSubPremiseType.Club]: 'lucideUsers',
    [BuildingSubPremiseType.Coolroom]: 'lucideSnowflake',
    [BuildingSubPremiseType.Cottage]: 'qpxSubPremiseCottage',
    [BuildingSubPremiseType.Duplex]: 'qpxSubPremiseDuplex',
    [BuildingSubPremiseType.Flat]: 'qpxSubPremiseFlat',
    [BuildingSubPremiseType.Factory]: 'lucideFactory',
    [BuildingSubPremiseType.Garage]: 'qpxSubPremiseGarage',
    [BuildingSubPremiseType.Hall]: 'qpxSubPremiseHall',
    [BuildingSubPremiseType.House]: 'lucideHouse',
    [BuildingSubPremiseType.Kiosk]: 'qpxSubPremiseKiosk',
    [BuildingSubPremiseType.Lobby]: 'qpxSubPremiseLobby',
    [BuildingSubPremiseType.Loft]: 'qpxSubPremiseLoft',
    [BuildingSubPremiseType.Lot]: 'lucideLandPlot',
    [BuildingSubPremiseType.Office]: 'lucideBuilding',
    [BuildingSubPremiseType.Penthouse]: 'qpxSubPremisePenthouse',
    [BuildingSubPremiseType.Rear]: 'qpxSubPremiseRear',
    [BuildingSubPremiseType.Room]: 'lucideDoorOpen',
    [BuildingSubPremiseType.Reserve]: 'lucideTrees',
    [BuildingSubPremiseType.Suite]: 'qpxSubPremiseSuite',
    [BuildingSubPremiseType.Section]: 'lucideLandPlot',
    [BuildingSubPremiseType.Shed]: 'qpxSubPremiseShed',
    [BuildingSubPremiseType.Shop]: 'lucideStore',
    [BuildingSubPremiseType.Showroom]: 'qpxSubPremiseShowroom',
    [BuildingSubPremiseType.Sign]: 'qpxSubPremiseSign',
    [BuildingSubPremiseType.Site]: 'lucideLandPlot',
    [BuildingSubPremiseType.Stall]: 'lucideStore',
    [BuildingSubPremiseType.Store]: 'lucideStore',
    [BuildingSubPremiseType.Studio]: 'qpxSubPremiseStudio',
    [BuildingSubPremiseType.StrataUnit]: 'qpxSubPremiseStrataUnit',
    [BuildingSubPremiseType.Substation]: 'qpxSubPremiseSubstation',
    [BuildingSubPremiseType.Tenancy]: 'lucideSquareUser',
    [BuildingSubPremiseType.Townhouse]: 'qpxSubPremiseTownhouse',
    [BuildingSubPremiseType.Tower]: 'qpxSubPremiseTower',
    [BuildingSubPremiseType.Unit]: 'qpxSubPremiseUnit',
    [BuildingSubPremiseType.Villa]: 'qpxSubPremiseVilla',
    [BuildingSubPremiseType.Vault]: 'qpxSubPremiseVault',
    [BuildingSubPremiseType.Ward]: 'qpxSubPremiseWard',
    [BuildingSubPremiseType.Lease]: 'lucideFileKey',
    [BuildingSubPremiseType.MarineBerth]: 'qpxSubPremiseMarineBerth',
    [BuildingSubPremiseType.Maisonette]: 'qpxSubPremiseMaisonette',
    [BuildingSubPremiseType.Warehouse]: 'lucideWarehouse',
    [BuildingSubPremiseType.Workshop]: 'lucideWrench',
} as const satisfies Record<BuildingSubPremiseTypeValue, IconName>;

export function getBuildingSubPremiseTypeIcon(
    value: BuildingSubPremiseTypeValue | null | undefined
): IconName {
    return (
        icons[value ?? BuildingSubPremiseType.Unknown] ??
        icons[BuildingSubPremiseType.Unknown]
    );
}

export function getBuildingIcon(
    primaryPropertyType: BuildingPrimaryPropertyType | null | undefined,
    isSecondaryAddress: boolean,
    subPremiseType: BuildingSubPremiseTypeValue | null | undefined
): IconName {
    return isSecondaryAddress
        ? getBuildingSubPremiseTypeIcon(subPremiseType)
        : getBuildingPropertyTypeIcon(primaryPropertyType);
}

@Pipe({ name: 'qpxBuildingSubPremiseTypeIcon' })
export class BuildingSubPremiseTypeIconPipe implements PipeTransform {
    public transform(
        value: BuildingSubPremiseTypeValue | null | undefined
    ): IconName {
        return getBuildingSubPremiseTypeIcon(value);
    }
}

@Pipe({ name: 'qpxBuildingIcon' })
export class BuildingIconPipe implements PipeTransform {
    public transform(
        primaryPropertyType: BuildingPrimaryPropertyType | null | undefined,
        isSecondaryAddress: boolean,
        subPremiseType: BuildingSubPremiseTypeValue | null | undefined
    ): IconName {
        return getBuildingIcon(
            primaryPropertyType,
            isSecondaryAddress,
            subPremiseType
        );
    }
}
